import { ViewConfig } from "@lib/views";
import {
    DATE_COLUMN_SIZE,
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { Category } from "@common/types/category";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import { HistoryTransactionCategoryCell } from "../cells/HistoryTransactionCategoryCell";
import { HistoryCreatedTransactionLedgerCell } from "../cells/HistoryCreatedTransactionLedgerCell";
import { HistoryDetailsDateCell } from "../../cells/HistoryDetailsDateCell";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDetailsAmountCell } from "../../cells/HistoryDetailsAmountCell";
import { HistoryCreatedTransactionFinancialAccountCell } from "../cells/HistoryCreatedTransactionFinancialAccountCell";
import { HistoryCreatedTransactionActionsCell } from "../cells/HistoryCreatedTransactionActionsCell";

/**
 * Cash-ledger account info shown in the GL-first "Cash ledger" column. Sourced
 * from the transaction's `accountId` diff: `accountId` is the GL account ID used
 * to resolve the live account, and `accountLabel` is the diff's human-readable
 * label used as a fallback when the account can no longer be resolved.
 */
export interface HistoryCreatedTransactionLedgerInfo {
    accountId?: string;
    accountLabel?: string;
}

export interface HistoryCreatedTransactionDetailsRow extends HistoryDetailsBaseRow {
    /** False for deleted / REMOVED rows — no drawer, no checkbox, no arrow. */
    canOpenTransaction: boolean;
    id: string;
    logDate: Date;
    date: Date;
    bankDescription: string;
    category: Partial<Category>;
    financialAccount: Partial<FinancialAccount>;
    amount: number;
    entity: Partial<Entity>;
    ledger: HistoryCreatedTransactionLedgerInfo;
}

export enum HistoryCreatedTransactionColumn {
    DATE = "date",
    BANK_DESCRIPTION = "bankDescription",
    CATEGORY = "category",
    LEDGER = "ledger",
    ACCOUNT = "financialAccount",
    AMOUNT = "amount",
    ENTITY = "entity",
    LOG_TIME = "logTime",
    ACTIONS = "actions",
}

type HistoryCreatedTransactionDetailsViewConfig = ViewConfig<
    HistoryCreatedTransactionDetailsRow,
    string,
    never
>;

const LEDGER_CLASSIFICATION_COLUMN: HistoryCreatedTransactionDetailsViewConfig["columns"] =
    {
        [HistoryCreatedTransactionColumn.LEDGER]: {
            accessorKey: "ledger",
            name: "Cash ledger",
            header: "Cash ledger",
            cell: HistoryCreatedTransactionLedgerCell,
            ...CATEGORY_COLUMN_SIZE,
        },
    };

const CATEGORY_CLASSIFICATION_COLUMN: HistoryCreatedTransactionDetailsViewConfig["columns"] =
    {
        [HistoryCreatedTransactionColumn.CATEGORY]: {
            accessorKey: "category",
            name: "Category",
            header: "Category",
            cell: HistoryTransactionCategoryCell,
            ...CATEGORY_COLUMN_SIZE,
        },
    };

const createHistoryCreatedTransactionDetailsViewConfig = (
    classificationColumn: HistoryCreatedTransactionDetailsViewConfig["columns"],
): HistoryCreatedTransactionDetailsViewConfig => ({
    columns: {
        [HistoryCreatedTransactionColumn.LOG_TIME]: {
            accessorKey: "logDate",
            name: "Time",
            header: "",
            cell: LogTimeCell,
            minSize: 120,
            maxSize: 150,
            immutable: true,
            hiddenFromSettings: true,
        },
        [HistoryCreatedTransactionColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            cell: HistoryDetailsDateCell,
            immutable: true,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryCreatedTransactionColumn.BANK_DESCRIPTION]: {
            accessorKey: "bankDescription",
            name: "Bank description",
            header: "Bank description",
            minSize: 200,
            maxSize: 600,
        },
        ...classificationColumn,
        [HistoryCreatedTransactionColumn.ACCOUNT]: {
            accessorKey: "financialAccount",
            name: "Account",
            header: "Account",
            cell: HistoryCreatedTransactionFinancialAccountCell,
            minSize: 200,
            maxSize: 400,
        },
        [HistoryCreatedTransactionColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: HistoryDetailsAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [HistoryCreatedTransactionColumn.ENTITY]: {
            accessorKey: "entity",
            name: "Entity",
            header: "Entity",
            cell: HistoryDetailsEntityCell,
            minSize: 56,
            maxSize: 150,
        },
        [HistoryCreatedTransactionColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: HistoryCreatedTransactionActionsCell,
            minSize: 56,
            maxSize: 56,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
});

export const createGlFirstHistoryCreatedTransactionDetailsViewConfig =
    (): HistoryCreatedTransactionDetailsViewConfig =>
        createHistoryCreatedTransactionDetailsViewConfig(
            LEDGER_CLASSIFICATION_COLUMN,
        );

export const createCategoryFirstHistoryCreatedTransactionDetailsViewConfig =
    (): HistoryCreatedTransactionDetailsViewConfig =>
        createHistoryCreatedTransactionDetailsViewConfig(
            CATEGORY_CLASSIFICATION_COLUMN,
        );
